// public/js/user/userManager.js
import BaseManager from '@base/baseManager.js';
import UserObject from './userObject.js';

class UserManager extends BaseManager {
    constructor() {
        // Call parent constructor with UserObject class
        super(UserObject, {
            entityType: 'user',
            cacheMaxAge: 300000, // 5 minutes
            autoCleanup: true
        });

        this.cardActionVisibility = {
            view: false,
            edit: true,
            delete: false,
            copy: false,
            print: false,
            download: false,
            share: false,
            unlink: false
        };
        
        this.logger.info('UserManager initialized with schema-driven support');
    }

    // ===============================
    // USER-SPECIFIC BUSINESS LOGIC ONLY
    // ===============================

    /**
     * Get user's display name
     * @param {UserObject} user - User object
     * @returns {string} Display name
     */
    getDisplayName(user) {
        if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        } else if (user.firstName) {
            return user.firstName;
        } else if (user.lastName) {
            return user.lastName;
        } else {
            return user.email.split('@')[0];
        }
    }

    /**
     * Get user's full name
     * @param {UserObject} user - User object
     * @returns {string} Full name
     */
    getFullName(user) {
        return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }

    /**
     * Check if user is active (email verified and admin activated)
     * @param {UserObject} user - User object
     * @returns {boolean} True if user is active
     */
    isUserActive(user) {
        return user.emailVerified && user.adminActivated;
    }

    /**
     * Get user status display text
     * @param {UserObject} user - User object
     * @returns {string} Status display text
     */
    getUserStatusDisplay(user) {
        if (!user.emailVerified) {
            return 'Email not verified';
        }
        if (!user.adminActivated) {
            return 'Pending admin activation';
        }
        return 'Active';
    }

    /**
     * Get role display name
     * @param {UserObject} user - User object
     * @returns {string} Role display name
     */
    getRoleDisplayName(user) {
        const roleNames = {
            'user': 'User',
            'pro': 'Pro User',
            'admin': 'Administrator'
        };
        return roleNames[user.role] || user.role;
    }

    /**
     * Get gender display name (German)
     * @param {UserObject} user - User object
     * @returns {string} Gender display name
     */
    getGenderDisplayName(user) {
        const genderNames = {
            'male': 'Männlich',
            'female': 'Weiblich',
            'other': 'Andere',
            'prefer_not_to_say': 'Keine Angabe'
        };
        return genderNames[user.gender] || user.gender || 'Keine Angabe';
    }

    /**
     * Check if user has pro privileges (prouser or admin)
     * @param {UserObject} user - User object
     * @returns {boolean} True if user has pro privileges
     */
    isProUser(user) {
        return user.role === 'pro' || user.role === 'admin';
    }

    /**
     * Calculate days since account creation
     * @param {UserObject} user - User object
     * @returns {number} Days since creation
     */
    getDaysSinceCreation(user) {
        if (!user.createdAt) return 0;
        
        try {
            const created = new Date(user.createdAt);
            const now = new Date();
            const diffTime = Math.abs(now - created);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays;
        } catch (error) {
            this.logger.error('Error calculating days since creation:', error);
            return 0;
        }
    }

    /**
     * Check if user has logged in recently (within last 30 days)
     * @param {UserObject} user - User object
     * @returns {boolean} True if recent login
     */
    hasRecentLogin(user) {
        if (!user.lastLogin) return false;
        
        try {
            const lastLogin = new Date(user.lastLogin);
            const now = new Date();
            const diffTime = Math.abs(now - lastLogin);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays <= 30;
        } catch (error) {
            return false;
        }
    }

    // ===============================
    // OVERRIDE BUSINESS LOGIC FOR USERS
    // ===============================

    /**
     * Override processFormData to add user-specific processing
     * @param {Object} formData - Raw form data
     * @returns {Object} Processed form data
     */
    processFormData(formData) {
        // First, use base schema-driven processing
        let processedData = super.processFormData(formData);
        
        // Normalize email to lowercase
        if (processedData.email) {
            processedData.email = processedData.email.toLowerCase().trim();
        }
        
        // Trim and normalize names
        if (processedData.firstName) {
            processedData.firstName = processedData.firstName.trim();
        }
        if (processedData.lastName) {
            processedData.lastName = processedData.lastName.trim();
        }

        // Validate and normalize gender
        if (processedData.gender) {
            const validGenders = ['male', 'female', 'other', 'prefer_not_to_say'];
            if (!validGenders.includes(processedData.gender)) {
                processedData.gender = 'prefer_not_to_say';
            }
        } else {
            processedData.gender = 'prefer_not_to_say';
        }
        
        return processedData;
    }

    /**
     * Override validate to add user-specific validation rules
     * @param {UserObject} user - User object to validate
     * @returns {Object} Validation result
     */
    validate(user) {
        // First, use base schema-driven validation
        const baseValidation = super.validate(user);
        const customErrors = [];

        // User-specific business rules
        
        // Business rule: Email should be unique (would need to check against server)
        // This would typically be handled server-side
        
        // Business rule: Name validation
        if (user.firstName && user.lastName) {
            const fullName = `${user.firstName} ${user.lastName}`;
            if (fullName.length > 100) {
                customErrors.push('Full name (first + last) must be less than 100 characters');
            }
        }

        // Business rule: Role consistency
        if (user.role === 'admin' && !user.adminActivated) {
            customErrors.push('Admin users must be activated');
        }

        // Business rule: Gender validation
        if (user.gender) {
            const validGenders = ['male', 'female', 'other', 'prefer_not_to_say'];
            if (!validGenders.includes(user.gender)) {
                customErrors.push('Invalid gender value');
            }
        }

        return {
            isValid: baseValidation.isValid && customErrors.length === 0,
            errors: [...baseValidation.errors, ...customErrors]
        };
    }

    // ===============================
    // OVERRIDE: USER-SPECIFIC DISPLAY PROCESSING
    // ===============================

    /**
     * Override processDisplayFieldValue for user-specific field processing
     * @param {UserObject} user - User object
     * @param {string} fieldName - Field name  
     * @param {Object} fieldSchema - Field schema
     * @returns {string} Processed field value
     */
    processDisplayFieldValue(user, fieldName, fieldSchema) {
        // User-specific field processing
        switch (fieldName) {
            case 'role':
                return this.getRoleDisplayName(user);
            case 'gender':
                return this.getGenderDisplayName(user);
            case 'emailVerified':
            case 'adminActivated':
                return user[fieldName] ? 'Yes' : 'No';
            case 'lastLogin':
                if (!user[fieldName]) return 'Never';
                return super.processDisplayFieldValue(user, fieldName, fieldSchema);
            default:
                // Use base processing for all other fields (including dates)
                return super.processDisplayFieldValue(user, fieldName, fieldSchema);
        }
    }

    /**
     * Override getCalculatedFieldsForSection for user-specific calculated fields
     * @param {UserObject} user - User object
     * @param {string} sectionKey - Section key
     * @returns {Array} Array of calculated field objects
     */
    getCalculatedFieldsForSection(user, sectionKey) {
        const calculatedFields = [];
        
        switch (sectionKey) {
            case 'basic_info':
                // Add display name
                calculatedFields.push({
                    name: 'displayName',
                    label: 'Display Name',
                    value: this.getDisplayName(user)
                });
                break;
                
            case 'status_info':
                // Add overall status
                calculatedFields.push({
                    name: 'overallStatus',
                    label: 'Overall Status',
                    value: this.getUserStatusDisplay(user)
                });
                
                // Add activity status
                calculatedFields.push({
                    name: 'activityStatus',
                    label: 'Activity Status',
                    value: this.hasRecentLogin(user) ? 'Active (recent login)' : 'Inactive (no recent login)'
                });
                break;
                
            case 'system_info':
                // Add days since creation
                const daysSinceCreation = this.getDaysSinceCreation(user);
                calculatedFields.push({
                    name: 'daysSinceCreation',
                    label: 'Account Age',
                    value: `${daysSinceCreation} days`
                });
                break;
        }
        
        return calculatedFields;
    }

    /**
     * Override prepareDetailsTitle for user-specific title formatting
     * @param {UserObject} user - User object
     * @param {Object} options - Options
     * @returns {Object} Title data
     */
    prepareDetailsTitle(user, options = {}) {
        return {
            main: this.getDisplayName(user),
            subtitle: user.email
        };
    }

    // ===============================
    // SCHEMA-DRIVEN DATA PREPARATION FOR VIEWS (Uses base methods)
    // ===============================

    /**
     * Prepare card data for view rendering - Uses base with user overrides
     * @param {UserObject} user - User object
     * @param {Object} options - Display options
     * @returns {Object} Card data ready for view
     */
    prepareCardData(user, options = {}) {
        // Get fields that should be shown in lists/cards from schema
        const listFields = this.getListFields();
        
        // Build fields array from schema using base processing
        const fields = [];
        
        listFields.forEach(fieldName => {
            const fieldSchema = this.getFieldSchema(fieldName);
            const value = this.processDisplayFieldValue(user, fieldName, fieldSchema);
            
            fields.push({
                name: fieldName,
                label: this.getFieldLabel(fieldName),
                value: value || 'Not specified'
            });
        });
        
        // Add calculated fields that are always useful for cards
        const overallStatus = this.getUserStatusDisplay(user);
        fields.push({
            name: 'overallStatus',
            label: 'Status',
            value: overallStatus
        });

        // Add gender if specified and not default
        if (user.gender && user.gender !== 'prefer_not_to_say') {
            fields.push({
                name: 'gender',
                label: 'Gender',
                value: this.getGenderDisplayName(user)
            });
        }

        return {
            id: user.id,
            header: {
                icon: this.getUserRoleIcon(user.role),
                status: {
                    type: this.isUserActive(user) ? 'active' : 'inactive',
                    text: overallStatus
                }
            },
            body: {
                title: this.getDisplayName(user),
                fields: fields
            },
            actions: this.getCardActions(user, options)
        };
    }

    /**
     * Get user role icon name
     * @param {string} role - User role
     * @returns {string} Material icon name
     */
    getUserRoleIcon(role) {
        const icons = {
            'user': 'person',
            'pro': 'star',
            'admin': 'admin_panel_settings'
        };
        return icons[role] || icons.user;
    }

    // ===============================
    // USER-SPECIFIC QUERIES
    // ===============================

    /**
     * Get users by role
     * @param {string} role - User role
     * @returns {Array} Array of matching users
     */
    getUsersByRole(role) {
        return this.findBy('role', role);
    }

    /**
     * Get users by gender
     * @param {string} gender - User gender
     * @returns {Array} Array of matching users
     */
    getUsersByGender(gender) {
        return this.findBy('gender', gender);
    }

    /**
     * Get active users
     * @returns {Array} Array of active users
     */
    getActiveUsers() {
        return this.data_set.filter(user => this.isUserActive(user));
    }

    /**
     * Get inactive users
     * @returns {Array} Array of inactive users
     */
    getInactiveUsers() {
        return this.data_set.filter(user => !this.isUserActive(user));
    }

    /**
     * Get users with recent activity
     * @returns {Array} Array of users with recent login
     */
    getRecentlyActiveUsers() {
        return this.data_set.filter(user => this.hasRecentLogin(user));
    }

    /**
     * Search users with user-specific fields
     * @param {string} searchTerm - Term to search for
     * @returns {Array} Array of matching users
     */
    search(searchTerm) {
        const userSpecificFields = ['firstName', 'lastName', 'email'];
        return super.search(searchTerm, userSpecificFields);
    }

    /**
     * Get details actions
     * @param {UserObject} user - User object
     * @param {Object} options - Action options
     * @returns {Array} Array of action configurations
     */
    getDetailsActions(user, options = {}) {
        return this.getCardActions(user, options);
    }

    /**
     * Get user summary for display purposes
     * @param {UserObject} user - User object
     * @returns {Object} Summary object with key information
     */
    getSummary(user) {
        return {
            id: user.id,
            displayName: this.getDisplayName(user),
            email: user.email,
            gender: this.getGenderDisplayName(user),
            role: this.getRoleDisplayName(user),
            status: this.getUserStatusDisplay(user),
            isActive: this.isUserActive(user),
            daysSinceCreation: this.getDaysSinceCreation(user),
            hasRecentLogin: this.hasRecentLogin(user),
            created: user.createdAt,
            updated: user.updatedAt,
            lastLogin: user.lastLogin
        };
    }

    // ===============================
    // DEBUG FUNCTION (as requested)
    // ===============================

    /**
     * Debug function: Print all users from manager to console
     */
    debugPrintAllUsers() {
        const users = this.read(); // Get all users from manager

        this.logger.debug('=== USER MANAGER DEBUG ===');
        this.logger.debug(`Total users: ${users.length}`);
        users.forEach((user, index) => {
            this.logger.debug(`${index + 1}. ${this.getDisplayName(user)} (${user.email}) - Gender: ${this.getGenderDisplayName(user)} - Role: ${user.role} - Active: ${this.isUserActive(user) ? 'Yes' : 'No'}`);
        });
        this.logger.debug('=== END USER MANAGER DEBUG ===');
    }

    /**
     * Override prepareDetailsHeader for user-specific header
     * @param {UserObject} user - User object
     * @param {Object} options - Options
     * @returns {Object} Header data
     */
    prepareDetailsHeader(user, options = {}) {
        return {
            imageUrl: user.photo_url || this.getGenderBasedPlaceholder(user),
            altText: `Photo of ${this.getDisplayName(user)}`,
            gender: user.gender,
            useGenderFallback: !user.photo_url
        };
    }

    /**
     * Get gender-based placeholder image
     * @param {UserObject} user - User object
     * @returns {string|null} Placeholder image URL or null for Material icon
     */
    getGenderBasedPlaceholder(user) {
        switch (user.gender) {
            case 'male':
                return '/images/boy.png';
            case 'female':
                return '/images/girl.png';
            case 'other':
            case 'prefer_not_to_say':
            default:
                return null; // Use Material icon for other/prefer_not_to_say
        }
    }


}

// Create and export singleton instance
const userManager = new UserManager();
export default userManager;